// Whether.market — Network Configuration & Deployed Contract Addresses
// Supports: TON Testnet, TyJolt Local, TyJolt Testnet, TON Mainnet

// ─── Contract Addresses ──────────────────────────────────────────────────────
// TVM addresses are deterministic — same contracts on TON and TyJolt
// Source: /opt/whether/contracts/perps/deployments/local.json

export const CONTRACTS = {
  wTONMinter: 'EQBacgr0rlipK7oa_zS_I6sWgByyaLj7hnOCrnzHUO3AVXDr',
  OraclePublisher: 'EQD0zRciUPJ2EeLdP6tAvk8bzZrnijN_sGnyOCfR6tjhbz55',
  PerpVault: 'EQBnI01qfR-yJoISFKIpgVelgatanPa8sQVGIvQi0ZnGwHfL',
  InsuranceFund: 'EQAPtx3l3k9_vCLTUHaMvVhfnWftuSrnfNyhUUAahHhzR2Gx',
  PerpFactory: 'EQBKoWyXLD6Hp_3svceM-Ee88dcqYe9uvhK-AMkcRoXHTSJF',
  Liquidator: 'EQDzJGC4cDna3pD0WoUuQ57EmwO5CWWqXWrPmLJcMXrz2q09',
  PerpMarket: 'EQC3E3PyLvEvibemkGepLCUYmCJTrnrlW5AK6Lwgrvru34Rx',
} as const;

// ─── Network Types ───────────────────────────────────────────────────────────

export type NetworkMode = 'testnet' | 'tyjolt-local' | 'tyjolt-testnet' | 'mainnet';

export interface NetworkConfig {
  name: string;
  apiBase: string;
  explorerBase: string;
  contracts: typeof CONTRACTS;
  walletMode: 'tonconnect' | 'dev-keypair';
  chainType: 'ton' | 'tyjolt';
  faucetUrl?: string;
  producerUrl?: string;  // TyJolt only
}

// ─── Network Configurations ──────────────────────────────────────────────────

export const NETWORKS: Record<NetworkMode, NetworkConfig> = {
  testnet: {
    name: 'TON Testnet',
    apiBase: 'https://testnet.tonapi.io/v2',
    explorerBase: 'https://testnet.tonscan.org',
    contracts: CONTRACTS,
    walletMode: 'tonconnect',
    chainType: 'ton',
  },
  'tyjolt-local': {
    name: 'TyJolt Local',
    apiBase: '/tyjolt',  // Proxied via Caddy to localhost:8780
    explorerBase: '/tyjolt-explorer', // Proxied to localhost:3000
    contracts: CONTRACTS,
    walletMode: 'dev-keypair',
    chainType: 'tyjolt',
    faucetUrl: '/tyjolt-faucet',  // Proxied to localhost:8081
    producerUrl: '/tyjolt',
  },
  'tyjolt-testnet': {
    name: 'TyJolt Testnet',
    apiBase: 'https://api.whether.market/tyjolt',
    explorerBase: 'https://api.whether.market/tyjolt-explorer',
    contracts: CONTRACTS,
    walletMode: 'dev-keypair',
    chainType: 'tyjolt',
    faucetUrl: 'https://api.whether.market/tyjolt-faucet',
    producerUrl: 'https://api.whether.market/tyjolt',
  },
  mainnet: {
    name: 'TON Mainnet',
    apiBase: 'https://tonapi.io/v2',
    explorerBase: 'https://tonscan.org',
    contracts: CONTRACTS,  // Will be different addresses on mainnet
    walletMode: 'tonconnect',
    chainType: 'ton',
  },
};

// ─── Active Network ──────────────────────────────────────────────────────────

const envNetwork = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TON_NETWORK) || 'testnet';

function getInitialNetwork(): NetworkMode {
  // Check localStorage first (user preference)
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('whether-network');
    if (saved && saved in NETWORKS) return saved as NetworkMode;
    // Default to TyJolt for app.whether.market testers
    if (window.location.hostname === 'app.whether.market') return 'tyjolt-local';
  }
  // Fall back to env var
  if (envNetwork in NETWORKS) return envNetwork as NetworkMode;
  return 'testnet';
}

let _activeNetwork: NetworkMode = getInitialNetwork();

export function getActiveNetwork(): NetworkMode {
  return _activeNetwork;
}

export function setActiveNetwork(network: NetworkMode): void {
  _activeNetwork = network;
  if (typeof window !== 'undefined') {
    localStorage.setItem('whether-network', network);
  }
}

export function getNetworkConfig(): NetworkConfig {
  return NETWORKS[_activeNetwork];
}

// ─── Backwards-compatible exports ────────────────────────────────────────────
// These resolve dynamically based on the active network

export function getTonApiBase(): string {
  return getNetworkConfig().apiBase;
}

export function getExplorerBase(): string {
  return getNetworkConfig().explorerBase;
}

// Legacy static exports for code that hasn't been migrated yet
export const TON_API_BASE = NETWORKS.testnet.apiBase;
export const TON_EXPLORER_BASE = NETWORKS.testnet.explorerBase;

// ─── Gas Estimates ───────────────────────────────────────────────────────────

export const GAS = {
  DEPOSIT: '200000000',    // 0.2 TON
  WITHDRAW: '100000000',   // 0.1 TON
  OPEN_POSITION: '150000000', // 0.15 TON
  CLOSE_POSITION: '100000000', // 0.1 TON
} as const;

// Token types for PerpVault
export const TOKEN_TYPE = {
  TON: 0,
  WTON: 1,
  USDT: 2,
} as const;

// ─── TyJolt Dev Accounts ────────────────────────────────────────────────────
// Pre-funded genesis accounts for TyJolt local/testnet dev mode

export const TYJOLT_DEV_ACCOUNTS = {
  TRADER_1: '0xAAAA',    // 1 billion RAE
  TRADER_2: '0xBBBB',    // 1 billion RAE
  TRADER_3: '0xCCCC',    // 1 billion RAE
  TRADER_4: '0xDDDD',    // 500 million RAE
  FAUCET: '0xFAUCET',    // 1 billion RAE
} as const;
